import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import { DelayIcon, InfoIcon } from 'foundations-assets';
import { getProjectedTotalMessage } from './getProjectedTotalMessage';
import { useGetCurrencyFormatting } from 'crm-object-board-core/multiCurrency';
import { AGG } from 'crm-pipeline-board-settings-lib/aggregations/types/Aggregations';
import { useStagesWithReconciledObjects } from '../../../reconciliation/useStagesWithReconciledObjects';
import { useProperty } from 'crm-object-board-core/properties';
import emptyFunction from 'react-utils/emptyFunction';
import { useCallback } from 'react';
import FormattedPropertyV2 from 'customer-data-properties/formatted/FormattedPropertyV2';
import { useHasCardV2 } from 'crm-object-board-core/hooks';
import { useObjectTypeId } from 'crm-object-board-core/objectTypeDefinition';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const alwaysUndefined = emptyFunction.thatReturns(undefined);
const getCurrencyTotal = aggregation => {
  if (!aggregation) {
    return null;
  }
  if (aggregation.meta && aggregation.meta.preferredCurrencyTotal) {
    return aggregation.meta.preferredCurrencyTotal;
  }
  return aggregation.value;
};
const getTotalProperty = (aggregation, config) => {
  if (aggregation && aggregation.meta && aggregation.meta.preferredCurrencyTotal !== undefined) {
    return config.meta.currencyProperty;
  }
  return config.property;
};
const AggregationValueWrapper = ({
  children
}) => {
  const hasBoardCardV2 = useHasCardV2();
  return hasBoardCardV2 ? /*#__PURE__*/_jsx(Text, {
    typography: "heading-25",
    as: "span",
    children: children
  }) : /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
const AggregationTextWrapper = ({
  children
}) => {
  const hasBoardCardV2 = useHasCardV2();
  return hasBoardCardV2 ? /*#__PURE__*/_jsx(Text, {
    typography: "detail-100",
    as: "span",
    children: children
  }) : /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
const Footer = ({
  aggregation,
  metadata,
  config,
  totalCards,
  stageId,
  currencyOptions
}) => {
  const agg = aggregation;
  const currencyTotal = getCurrencyTotal(agg) || 0;
  const objectTypeId = useObjectTypeId();
  const totalPropertyName = getTotalProperty(agg, config);
  const totalProperty = useProperty(totalPropertyName);
  const getCurrencyFormatting = useGetCurrencyFormatting();
  const probability = metadata && metadata.probability || '0';
  const isAverage = config.aggregation === AGG.AVG;
  const isProjected = config.meta.projected;
  const stages = useStagesWithReconciledObjects();
  const stageHasReconciledRecords = Boolean(stages.has(stageId));
  const hasBoardCardV2 = useHasCardV2();
  const getDraftValue = useCallback(property => {
    if (property === currencyOptions.currencyCodeProperty) {
      return currencyOptions.preferredCurrencyCode;
    }
    return undefined;
  }, [currencyOptions.currencyCodeProperty, currencyOptions.preferredCurrencyCode]);
  if (!totalProperty) {
    return null;
  }
  if (isProjected) {
    return /*#__PURE__*/_jsxs(AggregationTextWrapper, {
      children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
        message: getProjectedTotalMessage(probability, hasBoardCardV2),
        options: {
          value: /*#__PURE__*/_jsx(AggregationValueWrapper, {
            children: /*#__PURE__*/_jsx(FormattedPropertyV2, {
              objectTypeId: objectTypeId,
              objectId: undefined,
              property: totalProperty,
              value: String(currencyTotal),
              getDraftValue: getDraftValue,
              getSavedValue: alwaysUndefined,
              currencyProps: Object.assign({}, getCurrencyFormatting(currencyTotal), {
                simplifyCurrency: false
              })
            })
          }),
          probability: /*#__PURE__*/_jsx(FormattedPercentage, {
            value: +probability * 100
          })
        }
      }), ' ', stageHasReconciledRecords ? /*#__PURE__*/_jsx(DelayIcon, {
        color: "icon-core-subtle",
        alignWithText: true
      }) : /*#__PURE__*/_jsx(InfoIcon, {
        color: "icon-core-subtle",
        alignWithText: true
      })]
    });
  }
  if (isAverage) {
    const average = currencyTotal / (totalCards || 1);
    return /*#__PURE__*/_jsx(AggregationTextWrapper, {
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: hasBoardCardV2 ? 'crmObjectBoard.deals.averageText' : 'crmObjectBoard.deals.average',
        options: {
          value: /*#__PURE__*/_jsx(AggregationValueWrapper, {
            children: /*#__PURE__*/_jsx(FormattedPropertyV2, {
              objectTypeId: objectTypeId,
              objectId: undefined,
              property: totalProperty,
              value: String(average),
              getDraftValue: getDraftValue,
              getSavedValue: alwaysUndefined,
              currencyProps: Object.assign({}, getCurrencyFormatting(average), {
                simplifyCurrency: false
              })
            })
          })
        }
      })
    });
  }
  return /*#__PURE__*/_jsx(AggregationTextWrapper, {
    children: /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: hasBoardCardV2 ? 'crmObjectBoard.deals.totalText' : 'crmObjectBoard.deals.total',
      options: {
        value: /*#__PURE__*/_jsx(AggregationValueWrapper, {
          children: /*#__PURE__*/_jsx(FormattedPropertyV2, {
            objectTypeId: objectTypeId,
            objectId: undefined,
            property: totalProperty,
            value: String(currencyTotal),
            getDraftValue: getDraftValue,
            getSavedValue: alwaysUndefined,
            currencyProps: Object.assign({}, getCurrencyFormatting(currencyTotal), {
              simplifyCurrency: false
            })
          })
        })
      }
    })
  });
};
export default Footer;